import React, { useState } from 'react';
import { Layout, Menu, Avatar, Tooltip, Tag, Progress } from 'antd';
import type { MenuProps } from 'antd';
import {
  LayoutDashboard,
  Sun,
  Kanban,
  CalendarDays,
  Bell,
  Bot,
  BotMessageSquare,
  Users,
  UsersRound,
  Settings,
  Building2,
  Target,
  Phone,
  Handshake,
  Radar,
  Workflow,
  Zap,
  Search,
  PhoneCall,
  Calculator,
  TrendingUp,
  Megaphone,
  Mail,
  Send,
  HeartPulse,
  FileText,
  Award,
  LineChart,
  BarChart3,
  MessageSquare,
  Package,
  FileSignature,
  FileCheck,
  ListChecks,
  CheckSquare,
  X,
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '../../stores/useAppStore';
import { useSupportUnread } from '../../../modules/support/hooks/useSupportUnread';
import { useRadarCount } from '../../../modules/radar/hooks/useRadarCount';
import { useAuth } from '../../contexts/AuthContext';
import { useProfilePhoto } from '../../hooks/useProfilePhoto';
import { useCreditStatus } from '../../hooks/useCreditStatus';
import { useRecommendations } from '../../../modules/settings/hooks/useRecommendations';
import { useCanAccess } from '../../hooks/useCanAccess';
import { useActiveTenant } from '../../hooks/useActiveTenant';
import { useAccountModules, type ModuleKey } from '../../hooks/useAccountModules';
import JanusLogoMark from '../JanusLogoMark';
import { useFeatureFlagsContext } from '../../features';
import { canonicalizeRoles, displayRolePtBr, isPlatformAdmin } from '../../rbac/roles';
import { ROUTES } from '../../constants/routes';

const { Sider } = Layout;

const JanusLogo: React.FC<{ collapsed: boolean }> = ({ collapsed }) => (
  <div
    className="flex items-center py-4 px-3"
    style={{ height: 64, gap: 10 }}
  >
    <JanusLogoMark size={32} />
    {!collapsed && (
      <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1, whiteSpace: 'nowrap' }}>
        <span style={{ color: '#F8FAFC', fontSize: 14, fontWeight: 900, letterSpacing: '0.08em' }}>
          JANUSOS.AI
        </span>
        <span style={{ color: 'rgba(165,180,252,0.5)', fontSize: 6, fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', marginTop: 2 }}>
          Sales Intelligence
        </span>
      </div>
    )}
  </div>
);

interface MenuItem {
  key: string;
  icon: React.ReactNode;
  labelKey: string;
  /** Canonical roles allowed to see this entry. Platform admins always see everything. */
  roles?: string[];
  /** Wave A permission gate. When set, item is shown iff the user has the permission. */
  permission?: string;
  badge?: boolean;
  feature?: string;
  /**
   * WP-FE.2 — account-module display gate. When set, the item is hidden iff
   * the account is NOT licensed for this module. Fail-open: while the modules
   * query is loading or on error, the item stays visible. This is DISPLAY
   * gating only — the real authorization gate lives in the backend (WP-0.2).
   * Items WITHOUT `module` are unaffected (purely additive).
   */
  module?: ModuleKey;
  /** When true, item key contains a dynamic segment we patch on render. */
  dynamicTenant?: boolean;
  /** localStorage key — when present, shows a "News" pulse badge until the user visits the route. */
  newsBadgeKey?: string;
}

interface MenuSection {
  labelKey: string;
  items: MenuItem[];
}

// Menu organizado POR ÁREA DE TRABALHO (contexto), não por tipo técnico.
// Cada seção agrupa o que o usuário faz junto no dia-a-dia. As duas seções
// de configuração no fim separam explicitamente o que é ACCOUNT-scoped
// (vale pra conta inteira / todos os tenants) do que é TENANT-scoped (vale
// só pro tenant atual) — respondendo a pergunta de escopo do produto.
const menuSections: MenuSection[] = [
  // ── PRINCIPAL — uso diário, transversal a qualquer papel ───────────────
  {
    labelKey: 'sidebar.sections.principal',
    items: [
      { key: '/', icon: <LayoutDashboard size={16} />, labelKey: 'sidebar.dashboard' },
      { key: ROUTES.MEU_DIA, icon: <Sun size={16} />, labelKey: 'sidebar.meuDia' },
      { key: '/agent', icon: <Bot size={16} />, labelKey: 'sidebar.agent', feature: 'agent' },
      { key: '/pipeline', icon: <Kanban size={16} />, labelKey: 'sidebar.pipeline', feature: 'pipeline' },
      { key: ROUTES.ACTIVITIES, icon: <CheckSquare size={16} />, labelKey: 'sidebar.activities' },
      { key: ROUTES.CALENDAR, icon: <CalendarDays size={16} />, labelKey: 'sidebar.calendar', feature: 'calendar' },
      { key: '/inbox', icon: <MessageSquare size={16} />, labelKey: 'sidebar.inbox', feature: 'messaging_inbox' },
      { key: '/alerts', icon: <Bell size={16} />, labelKey: 'sidebar.alerts', feature: 'alerts' },
    ],
  },
  // ── SALES WORKBENCH — prospecção → fechamento (BDR/SDR/Executivo) ───────
  {
    labelKey: 'sidebar.sections.salesWorkbench',
    items: [
      { key: ROUTES.RADAR, icon: <Radar size={16} />, labelKey: 'sidebar.radar', feature: 'radar',
        roles: ['bdr', 'sdr', 'executivo', 'gestor', 'diretor', 'tenant_admin', 'account_owner', 'super_admin', 'master_admin'] },
      // Autopilot + Cockpit — gated by the premium `autopilot` add-on (module
      // license) ON TOP of the `radar` feature + roles. All three gates AND:
      // an account without the add-on never sees these entries. Fail-open only
      // if /account/modules is unreachable (the sales-api runtime gate is the
      // real boundary — see PR #435).
      { key: ROUTES.AUTOPILOT, icon: <Zap size={16} />, labelKey: 'sidebar.autopilot', feature: 'radar', module: 'autopilot',
        roles: ['bdr', 'sdr', 'executivo', 'gestor', 'diretor', 'tenant_admin', 'account_owner', 'super_admin', 'master_admin'] },
      { key: ROUTES.AUTOPILOT_COCKPIT, icon: <FileCheck size={16} />, labelKey: 'sidebar.autopilotCockpit', feature: 'radar', module: 'autopilot',
        roles: ['bdr', 'sdr', 'executivo', 'gestor', 'diretor', 'tenant_admin', 'account_owner', 'super_admin', 'master_admin'] },
      // "Automações ativas" (auditoria UX+PM 2026-08-31) — 4ª irmã de
      // fila/cockpit/execuções: a pergunta "meu Autopilot está rodando, e
      // quando ele avança?" tenant-wide, hoje sem resposta em tela nenhuma
      // (o Sales Workbench tem o mesmo dado atrás de um Popover por deal).
      // Mesmos gates das outras duas — é o mesmo add-on, a mesma pergunta.
      { key: ROUTES.AUTOPILOT_DEALS, icon: <ListChecks size={16} />, labelKey: 'sidebar.autopilotDeals', feature: 'radar', module: 'autopilot',
        roles: ['bdr', 'sdr', 'executivo', 'gestor', 'diretor', 'tenant_admin', 'account_owner', 'super_admin', 'master_admin'] },
      // Biblioteca de Modelos de Fluxo (flow-templates Fase 1) — vizinha do
      // Autopilot porque é o mesmo gesto ("desenhar a régua"), mas SEM o gate
      // de módulo `autopilot`: o modelo salvo também alimenta a cadência do
      // deal e a régua de nutrição, que não são do add-on. Sem gate de papel —
      // o escopo real é o tenant, aplicado pela própria API.
      { key: ROUTES.FLOW_TEMPLATES, icon: <Workflow size={16} />, labelKey: 'sidebar.flowTemplates' },
      { key: '/prospects?tab=discovery', icon: <Search size={16} />, labelKey: 'sidebar.prospects', feature: 'prospects' },
      // "Qualificação" menu item removed (owner request, 2026-08-13) — it
      // duplicated the /prospects route already covered by the "Lead" entry
      // above; the qualification workspace stays reachable via /prospects.
      { key: ROUTES.SALES_REP, icon: <Search size={16} />, labelKey: 'sidebar.bdr', feature: 'bdr', roles: ['bdr', 'account_owner', 'tenant_admin', 'gestor', 'diretor', 'super_admin', 'master_admin'], newsBadgeKey: 'janus.salesWorkbench.announcement.v1' },
      { key: ROUTES.SALES_REP, icon: <Phone size={16} />, labelKey: 'sidebar.sdr', feature: 'sdr', roles: ['sdr', 'tenant_admin', 'gestor', 'diretor', 'super_admin', 'master_admin'], newsBadgeKey: 'janus.salesWorkbench.announcement.v1' },
      { key: ROUTES.SDR_COCKPIT, icon: <PhoneCall size={16} />, labelKey: 'sidebar.sdrCaller', feature: 'sdr_agent', roles: ['sdr', 'gestor', 'diretor', 'executivo', 'tenant_admin', 'account_owner', 'super_admin', 'master_admin'], newsBadgeKey: 'janus.sdrCaller.announcement.v1' },
      // Equipe de Agentes — Agente de Mensageria (onda 1). Ao lado do SDR
      // Caller de propósito: é o mesmo gesto ("contratar um agente"), num
      // canal diferente. Dark: a flag `agente_mensageria` é `defaultEnabled:
      // false` E entitlement-gated, então o item some sozinho em qualquer
      // deploy que não o tenha ligado — sem `if` extra aqui.
      { key: ROUTES.MEET, icon: <CalendarDays size={16} />, labelKey: 'Janus Meet', feature: 'janus_meet', roles: ['bdr', 'sdr', 'executivo', 'gestor', 'diretor', 'tenant_admin', 'account_owner', 'super_admin', 'master_admin'] },
      { key: ROUTES.AGENTES, icon: <BotMessageSquare size={16} />, labelKey: 'sidebar.agentes', feature: 'agente_mensageria', roles: ['bdr', 'sdr', 'executivo', 'gestor', 'diretor', 'tenant_admin', 'account_owner', 'super_admin', 'master_admin'], newsBadgeKey: 'janus.agentes.announcement.v1' },
      // Telefonia (BYO GoTo) — logo abaixo do SDR Caller. SEM `roles` =
      // visível a TODOS os papéis inicialmente (os SDRs são os principais
      // usuários); segue gated pela flag `telephony` (plano Pro+/allowlist).
      { key: ROUTES.TELEPHONY_INTEGRATIONS, icon: <Phone size={16} />, labelKey: 'sidebar.telephony', feature: 'telephony' },
      { key: '/executive', icon: <Handshake size={16} />, labelKey: 'sidebar.executive', feature: 'executive' },
      { key: '/contacts', icon: <Users size={16} />, labelKey: 'sidebar.contacts', feature: 'contacts' },
      // WP-FE.2 — CRM module (HubSpot/Salesforce sync workspace). Display-gated
      // by the account's `crm` module license; fail-open when the modules query
      // is unavailable. Purely additive (no role/feature gate so the module
      // license is the only thing that can hide it).
      { key: ROUTES.CRM_INTEGRATIONS, icon: <Building2 size={16} />, labelKey: 'sidebar.crmModule', module: 'crm' },
      // "Templates de Email" moved to the Setup hub (Workspace/Tenant block) —
      // it is tenant-scoped configuration, consolidated under /setup.
    ],
  },
  // ── MARKETING / CAMPANHAS — workspace MKT (tenant-scoped) ──────────────
  // Renomeado de "MKT Workspace" → "Campanhas" (alinhamento c/ Salesforce
  // Campaigns). As integrações de canal (Google Ads/Meta/LinkedIn/WhatsApp)
  // SAÍRAM daqui — agora vivem no Hub unificado em /integracoes.
  {
    labelKey: 'sidebar.sections.mkt',
    items: [
      { key: '/mkt', icon: <Megaphone size={16} />, labelKey: 'sidebar.mkt', roles: ['mkt', 'account_owner', 'tenant_admin', 'gestor', 'diretor', 'super_admin', 'master_admin'], badge: true, feature: 'mkt' },
      // Biblioteca de templates de e-mail. SEM gate de role (qualquer papel
      // pode ver/usar templates — BDR/SDR/Executivo escrevem e-mails a partir
      // deles), apenas o feature gate `templates` que casa com a rota
      // (<FeatureRoute name="templates">). Saiu do Setup hub para o menu
      // Marketing a pedido do produto: era difícil de achar enterrado em
      // Setup & Configurações.
      { key: ROUTES.TEMPLATES, icon: <FileText size={16} />, labelKey: 'sidebar.mktTemplates', feature: 'templates' },
      { key: '/mkt/automation/sequences', icon: <Mail size={16} />, labelKey: 'sidebar.mktSequences', roles: ['mkt', 'account_owner', 'tenant_admin', 'gestor', 'diretor', 'admin', 'super_admin', 'master_admin'], feature: 'mkt' },
      // Campanhas de e-mail (W3, item 7) — ao lado das réguas de propósito:
      // no backend a campanha É uma régua (`trigger_type='scheduled_blast'`),
      // mesmo motor, mesmo guard. Endereço PRÓPRIO porque o vocabulário que a
      // pessoa vem operar aqui é o de campanha (público + horário UMA vez),
      // não o de sequência de toques.
      { key: ROUTES.MKT_EMAIL_CAMPAIGNS, icon: <Send size={16} />, labelKey: 'sidebar.mktEmailCampaigns', roles: ['mkt', 'account_owner', 'tenant_admin', 'gestor', 'diretor', 'admin', 'super_admin', 'master_admin'], feature: 'mkt' },
      // Saúde do e-mail (W4, 7b) — entregabilidade + supressões, ao lado das
      // outras telas de e-mail de propósito: é a mesma vizinhança que o
      // operador já vem para configurar/monitorar o canal.
      { key: ROUTES.MKT_EMAIL_HEALTH, icon: <HeartPulse size={16} />, labelKey: 'sidebar.mktEmailHealth', roles: ['mkt', 'account_owner', 'tenant_admin', 'gestor', 'diretor', 'admin', 'super_admin', 'master_admin'], feature: 'mkt' },
      { key: '/mkt/outcomes', icon: <Award size={16} />, labelKey: 'sidebar.mktOutcomes', roles: ['mkt', 'account_owner', 'tenant_admin', 'gestor', 'diretor', 'super_admin', 'master_admin'], badge: true, feature: 'mkt' },
      { key: '/mkt/incrementality', icon: <LineChart size={16} />, labelKey: 'sidebar.mktIncrementality', roles: ['mkt', 'account_owner', 'tenant_admin', 'gestor', 'diretor', 'super_admin', 'master_admin'], badge: true, feature: 'mkt' },
    ],
  },
  // ── RECEITA — funil comercial de fechamento (contas, propostas, contratos) ─
  {
    labelKey: 'sidebar.sections.receita',
    items: [
      { key: ROUTES.CONTAS, icon: <Building2 size={16} />, labelKey: 'sidebar.contas', feature: 'commercial_accounts',
        roles: ['account_owner', 'tenant_admin', 'super_admin', 'master_admin', 'diretor', 'gestor', 'executivo', 'bdr', 'sdr', 'mkt'] },
      { key: ROUTES.OPORTUNIDADES, icon: <Target size={16} />, labelKey: 'sidebar.oportunidades', feature: 'commercial_accounts',
        roles: ['account_owner', 'tenant_admin', 'super_admin', 'master_admin', 'diretor', 'gestor', 'executivo', 'bdr', 'sdr', 'mkt'] },
      { key: ROUTES.CROSS_SELL_IMPORT, icon: <Package size={16} />, labelKey: 'crossSell.sidebar', feature: 'cross_sell',
        roles: ['account_owner', 'tenant_admin', 'super_admin', 'master_admin', 'diretor', 'gestor'] },
      // "Importar catálogo" moved into the Produtos page as a tab
      // (/settings/products?tab=import). The old /admin/import-catalogo route
      // now redirects there.
      { key: ROUTES.CROSS_SELL_CAMPAIGNS, icon: <Megaphone size={16} />, labelKey: 'crossSell.campaignsSidebar', feature: 'cross_sell',
        roles: ['account_owner', 'tenant_admin', 'super_admin', 'master_admin', 'diretor', 'gestor'] },
      { key: ROUTES.PROPOSTAS, icon: <FileSignature size={16} />, labelKey: 'sidebar.propostas', feature: 'proposals',
        roles: ['account_owner', 'executivo', 'gestor', 'diretor', 'tenant_admin', 'super_admin', 'master_admin'] },
      { key: ROUTES.CONTRATOS, icon: <FileCheck size={16} />, labelKey: 'sidebar.contratos', feature: 'contracts',
        roles: ['account_owner', 'executivo', 'gestor', 'diretor', 'tenant_admin', 'super_admin', 'master_admin'] },
    ],
  },
  // ── INTELIGÊNCIA — análises e insights (ROI, win/loss, NPS) ─────────────
  {
    labelKey: 'sidebar.sections.analises',
    items: [
      { key: '/analytics/roi', icon: <Calculator size={16} />, labelKey: 'sidebar.roi',
        roles: ['account_owner', 'tenant_admin', 'gestor', 'diretor', 'executivo', 'mkt', 'super_admin', 'master_admin'], feature: 'roi_calculator' },
      { key: '/analytics/win-loss', icon: <TrendingUp size={16} />, labelKey: 'sidebar.winLoss',
        roles: ['account_owner', 'tenant_admin', 'gestor', 'diretor', 'executivo', 'super_admin', 'master_admin'], feature: 'winloss' },
      { key: '/admin/analytics', icon: <BarChart3 size={16} />, labelKey: 'sidebar.npsAnalytics',
        roles: ['account_owner', 'tenant_admin', 'gestor', 'diretor', 'super_admin', 'master_admin'], feature: 'nps' },
      { key: ROUTES.EQUIPE, icon: <UsersRound size={16} />, labelKey: 'sidebar.equipe',
        roles: ['account_owner', 'tenant_admin', 'gestor', 'diretor', 'super_admin', 'master_admin'] },
    ],
  },
  // "Integrações" (Hub /integracoes) moved to the Setup hub (Conta block) —
  // CRM/signature/ads integrations are account/tenant-scoped configuration,
  // consolidated under /setup.
  // ── SETUP & CONFIGURAÇÕES — hub único ──────────────────────────────────
  // As antigas seções CONFIG. DA CONTA / CONFIG. DO TENANT / PESSOAL foram
  // consolidadas numa única entrada que leva ao hub /setup. O gating fino
  // (roles + feature + permission) por item agora vive nos cards do hub
  // (SetupHubPage), então aqui a entrada é visível a todos os papéis — quem
  // não tiver nenhuma config disponível verá o estado vazio no hub.
  {
    labelKey: 'sidebar.sections.setup',
    items: [
      { key: ROUTES.SETUP, icon: <Settings size={16} />, labelKey: 'sidebar.setup' },
    ],
  },
];

const NovoBadge: React.FC<{ label: string }> = ({ label }) => (
  <Tag
    color="#6366F1"
    style={{
      fontSize: 9,
      lineHeight: '16px',
      padding: '0 4px',
      marginLeft: 6,
      borderRadius: 4,
      fontWeight: 600,
    }}
  >
    {label}
  </Tag>
);

const NEWS_BADGE_CSS = `
  @keyframes janus-news-glow {
    0%, 100% { box-shadow: 0 0 0 0 rgba(99,102,241,0); opacity: 1; }
    50%       { box-shadow: 0 0 0 4px rgba(99,102,241,0.25); opacity: 0.85; }
  }
  @keyframes janus-news-dot {
    0%, 100% { transform: scale(1); opacity: 1; }
    50%       { transform: scale(1.35); opacity: 0.7; }
  }
  .janus-news-badge {
    animation: janus-news-glow 2.4s ease-in-out infinite;
    display: inline-flex;
    align-items: center;
    gap: 3px;
    margin-left: 7px;
    padding: 1px 6px 1px 4px;
    border-radius: 10px;
    background: rgba(99,102,241,0.12);
    border: 1px solid rgba(99,102,241,0.35);
    color: #A5B4FC;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    line-height: 14px;
    vertical-align: middle;
    white-space: nowrap;
  }
  .janus-news-dot {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: #818CF8;
    display: inline-block;
    flex-shrink: 0;
    animation: janus-news-dot 2.4s ease-in-out infinite;
  }
  @media (prefers-reduced-motion: reduce) {
    .janus-news-badge { animation: none; }
    .janus-news-dot   { animation: none; }
  }
`;

const NewsBadge: React.FC = () => (
  <>
    <style>{NEWS_BADGE_CSS}</style>
    <span className="janus-news-badge">
      <span className="janus-news-dot" />
      News
    </span>
  </>
);

const CreditDot: React.FC<{ level: 'warning' | 'critical' }> = ({ level }) => (
  <span
    style={{
      display: 'inline-block',
      width: 8,
      height: 8,
      borderRadius: '50%',
      background: level === 'critical' ? '#f5222d' : '#faad14',
      marginLeft: 6,
      flexShrink: 0,
    }}
  />
);

const ONBOARDING_DISMISSED_KEY = 'janus_onboarding_hub_dismissed';

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sidebarCollapsed, setSidebarCollapsed } = useAppStore();
  const { user } = useAuth();
  const photo = useProfilePhoto();
  const { creditStatus } = useCreditStatus();
  const { t } = useTranslation();
  const { data: recommendations } = useRecommendations(user?.tenantId);
  const [onboardingDismissed, setOnboardingDismissed] = useState(
    () => localStorage.getItem(ONBOARDING_DISMISSED_KEY) === 'true',
  );

  const [seenNewsKeys, setSeenNewsKeys] = useState<Set<string>>(() => {
    const seen = new Set<string>();
    try {
      menuSections
        .flatMap((s) => s.items)
        .filter((i) => i.newsBadgeKey)
        .forEach((i) => {
          if (localStorage.getItem(i.newsBadgeKey!)) seen.add(i.newsBadgeKey!);
        });
    } catch { /* noop */ }
    return seen;
  });

  const dismissNewsBadge = (key: string) => {
    try {
      if (!localStorage.getItem(key)) {
        localStorage.setItem(key, new Date().toISOString());
      }
    } catch { /* noop */ }
    setSeenNewsKeys((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  };
  const { tenantId: activeTenantId } = useActiveTenant();
  const canAccountCustomRoles = useCanAccess('account.custom_roles.create');
  const canTenantCustomRoles = useCanAccess('tenant.custom_roles.create', activeTenantId ?? undefined);

  const u = (user as Record<string, unknown> | null) ?? {};
  const accountRole = (u.accountRole as string | undefined) ?? (u.account_role as string | undefined);
  const userRoleStrings: string[] = [
    ...((u.roles as string[] | undefined) ?? []),
    user?.role,
    accountRole,
  ].filter(Boolean) as string[];
  const userCanonical = new Set<string>(canonicalizeRoles(userRoleStrings));
  const userIsPlatformAdmin = isPlatformAdmin(userRoleStrings);

  const getSelectedKey = () => {
    const path = location.pathname;
    if (path === '/') return '/';
    const allItems = menuSections.flatMap((s) => s.items);
    const match = allItems.find(
      (item) => item.key !== '/' && path.startsWith(item.key.replace(':tenantId', activeTenantId ?? '')),
    );
    return match ? match.key : '/';
  };

  const { isEnabled: isFeatureEnabled } = useFeatureFlagsContext();
  // WP-FE.2 — account module licensing (display gate only; fail-open).
  const { isModuleLicensed } = useAccountModules();

  const filterByRoleAndFeature = <T extends MenuItem>(items: T[]): T[] =>
    items.filter((item) => {
      if (item.roles) {
        if (!userIsPlatformAdmin) {
          const allowed = canonicalizeRoles(item.roles);
          if (!allowed.some((r) => userCanonical.has(r))) return false;
        }
      }
      if (item.feature && !isFeatureEnabled(item.feature as import('../../features').FeatureKey)) return false;
      if (item.permission === 'account.custom_roles.create' && !canAccountCustomRoles) return false;
      if (item.permission === 'tenant.custom_roles.create' && !canTenantCustomRoles) return false;
      // Module gate (additive): hide only when the account is explicitly NOT
      // licensed for the item's module. Fail-open while loading / on error.
      if (item.module && !isModuleLicensed(item.module)) return false;
      return true;
    });

  const alertLevel = creditStatus?.alertLevel;
  const { unreadCount: supportUnreadCount } = useSupportUnread();
  const { count: radarCount } = useRadarCount();

  const renderLabel = (item: MenuItem) => {
    const label = t(item.labelKey);

    if (item.key === '/admin' && alertLevel && alertLevel !== 'ok') {
      return (
        <span style={{ display: 'flex', alignItems: 'center' }}>
          {label}
          <CreditDot level={alertLevel} />
        </span>
      );
    }

    if (item.key === '/admin/support/tickets' && supportUnreadCount > 0) {
      return (
        <span style={{ display: 'flex', alignItems: 'center' }}>
          <span className="support-inbox-blink" style={{ fontWeight: 600 }}>
            {label}
          </span>
          <span
            aria-label={`${supportUnreadCount} novas respostas`}
            className="support-inbox-blink"
            style={{
              marginLeft: 8,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 18,
              height: 18,
              padding: '0 6px',
              borderRadius: 9,
              fontSize: 11,
              fontWeight: 700,
              color: '#fff',
              background: '#EF4444',
            }}
          >
            {supportUnreadCount > 9 ? '9+' : supportUnreadCount}
          </span>
          <style>{`
            @keyframes support-inbox-blink-kf {
              0%, 100% { opacity: 1; }
              50%      { opacity: 0.35; }
            }
            .support-inbox-blink {
              animation: support-inbox-blink-kf 1.2s ease-in-out infinite;
            }
            @media (prefers-reduced-motion: reduce) {
              .support-inbox-blink { animation: none; }
            }
          `}</style>
        </span>
      );
    }

    if (item.key === ROUTES.RADAR && radarCount > 0) {
      return (
        <span style={{ display: 'flex', alignItems: 'center' }}>
          {label}
          <span
            aria-label={`${radarCount} novas recomendações`}
            style={{
              marginLeft: 8,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 18,
              height: 18,
              padding: '0 6px',
              borderRadius: 9,
              fontSize: 11,
              fontWeight: 700,
              color: '#fff',
              background: '#6366F1',
            }}
          >
            {radarCount > 9 ? '9+' : radarCount}
          </span>
        </span>
      );
    }

    if (item.newsBadgeKey && !seenNewsKeys.has(item.newsBadgeKey)) {
      return (
        <span style={{ display: 'flex', alignItems: 'center' }}>
          {label}
          <NewsBadge />
        </span>
      );
    }

    if (item.badge) {
      return (
        <span style={{ display: 'flex', alignItems: 'center' }}>
          {label}
          <NovoBadge label={t('sidebar.novo')} />
        </span>
      );
    }

    return label;
  };

  const resolveKey = (item: MenuItem): string => {
    if (item.dynamicTenant && activeTenantId) {
      return item.key.replace(':tenantId', activeTenantId);
    }
    return item.key;
  };

  const buildMenuItems = (): MenuProps['items'] => {
    if (sidebarCollapsed) {
      return menuSections.flatMap((section) =>
        filterByRoleAndFeature(section.items).map((item, idx) => ({
          key: `${item.key}-${item.labelKey}-${idx}`,
          icon: (
            <Tooltip title={t(item.labelKey)} placement="right">
              {item.icon}
            </Tooltip>
          ),
          label: renderLabel(item),
          onClick: () => {
            if (item.newsBadgeKey) dismissNewsBadge(item.newsBadgeKey);
            navigate(resolveKey(item));
          },
        })),
      );
    }

    return menuSections.flatMap((section, si) => [
      {
        type: 'group' as const,
        label: (
          <span
            data-tour={`sidebar-${section.labelKey.toLowerCase()}`}
            style={{
              fontSize: 10,
              fontWeight: 600,
              color: '#475569',
              letterSpacing: '0.8px',
              paddingLeft: 12,
            }}
          >
            {t(section.labelKey)}
          </span>
        ),
        children: filterByRoleAndFeature(section.items).map((item, idx) => ({
          key: `${item.key}-${item.labelKey}-${idx}`,
          icon: item.icon,
          label: renderLabel(item),
          onClick: () => {
            if (item.newsBadgeKey) dismissNewsBadge(item.newsBadgeKey);
            navigate(resolveKey(item));
          },
        })),
        key: `group-${si}`,
      },
    ]);
  };

  return (
    <Sider
      collapsible
      collapsed={sidebarCollapsed}
      onCollapse={setSidebarCollapsed}
      width={240}
      breakpoint="lg"
      style={{
        overflow: 'auto',
        height: '100vh',
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
        zIndex: 100,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <style>{`
        .janus-sidebar-menu .ant-menu-item,
        .janus-sidebar-menu .ant-menu-submenu-title {
          height: auto !important;
          min-height: 40px;
          line-height: 1.3 !important;
          padding-top: 6px !important;
          padding-bottom: 6px !important;
        }
        .janus-sidebar-menu .ant-menu-title-content {
          white-space: normal !important;
          word-break: break-word;
          line-height: 1.3;
          display: inline-block;
          vertical-align: middle;
        }
      `}</style>
      <JanusLogo collapsed={sidebarCollapsed} />

      <div style={{ flex: 1, overflow: 'auto' }}>
        <Menu
          theme="dark"
          mode="inline"
          className="janus-sidebar-menu"
          selectedKeys={[getSelectedKey()]}
          items={buildMenuItems()}
          style={{ border: 'none', paddingBottom: 8 }}
        />
      </div>

      {/* Onboarding hub widget */}
      {!onboardingDismissed && recommendations && recommendations.completudeScore < 100 && (
        <div
          style={{
            borderTop: '1px solid #1E293B',
            padding: sidebarCollapsed ? '8px 8px' : '10px 16px',
            cursor: 'pointer',
          }}
          onClick={() => navigate('/settings/onboarding')}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {!sidebarCollapsed && (
              <>
                <span style={{ color: '#F1F5F9', fontSize: 12, fontWeight: 500 }}>
                  Onboarding hub
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    localStorage.setItem(ONBOARDING_DISMISSED_KEY, 'true');
                    setOnboardingDismissed(true);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 2,
                    display: 'flex',
                    color: 'var(--color-text-secondary)',
                  }}
                >
                  <X size={14} />
                </button>
              </>
            )}
          </div>
          {!sidebarCollapsed ? (
            <>
              <Progress
                percent={recommendations.completudeScore}
                size="small"
                strokeColor="#6366F1"
                trailColor="#1E293B"
                showInfo={false}
                style={{ marginTop: 6, marginBottom: 4 }}
              />
              <span style={{ color: 'var(--color-text-tertiary)', fontSize: 11 }}>
                {recommendations.completudeScore}% Completado
              </span>
            </>
          ) : (
            <Tooltip title={`Onboarding: ${recommendations.completudeScore}%`} placement="right">
              <Progress
                type="circle"
                percent={recommendations.completudeScore}
                size={28}
                strokeColor="#6366F1"
                trailColor="#1E293B"
                format={(p) => <span style={{ color: '#F1F5F9', fontSize: 8 }}>{p}%</span>}
              />
            </Tooltip>
          )}
        </div>
      )}

      {/* User footer */}
      <div
        style={{
          borderTop: '1px solid #1E293B',
          padding: sidebarCollapsed ? '12px 8px' : '12px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <Avatar
          src={photo}
          size={32}
          style={{
            background: 'linear-gradient(135deg, #6366F1, #8B5CF6)',
            flexShrink: 0,
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {user?.name?.charAt(0)?.toUpperCase() || 'U'}
        </Avatar>
        {!sidebarCollapsed && (
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                color: '#F1F5F9',
                fontSize: 13,
                fontWeight: 500,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {user?.name || t('sidebar.user')}
            </div>
            <div
              style={{
                color: 'var(--color-text-secondary)',
                fontSize: 11,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {user ? displayRolePtBr(user.role) : 'user'}
            </div>
          </div>
        )}
      </div>
    </Sider>
  );
};

export default Sidebar;
